import moment from 'moment-timezone';
import errorReporter from '@app/errorReporter';
import { parseISO } from 'date-fns';
import { ROLES, UserRoles } from '@bb/users';
import { message } from 'components';
import { AuthContext } from 'contexts';
import React, { useEffect, useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router';
import {
  AppointmentConfigJsonType,
  EnhancedAppointment,
} from '@bb/domain/dist/types';
import type { CachedOrg } from '@bb/automaton';
import {
  isAssumeOrgType,
  isAuthForRole,
  isFullAccessUser,
  featureEnabled,
  isManualCoopUserWithAccess,
} from 'utils';
import {
  getAppointmentBookingInfo,
  getExternalAppointmentConfig,
} from '../../../api/AppointmentController';
import getPhoneNumber from '../../../api/LastPhoneNumberController/getPhoneNumber';
import { Listing } from '../../../api/ListingController/getListing';
import getOfferDetails from '../../../api/OfferController/getOfferDetails';
import type { OverrideOptionType } from './NoShowingsWrapper/types';

type GetExternalAppointmentConfigType = Awaited<
  ReturnType<typeof getExternalAppointmentConfig>
>;
type GetOfferDetailsType = Awaited<ReturnType<typeof getOfferDetails>>;
type GetLastPhoneNumberType = Awaited<ReturnType<typeof getPhoneNumber>>;
type AppointmentBookingInternalContextProps = { listingId: string };

export type ActionButtonData = {
  variant: 'book' | 'confirm' | 'autoConfirm' | 'record' | 'virtualTour';
  onClick: () => void;
  disabled: boolean;
};

type BuyerAgreementData = {
  agreementRequired: boolean;
  hasNarBuyerSettlement: boolean;
  isBuyerShowing: boolean;
  isRecordShowing: boolean;
  coopSelected: boolean;
};

export type AppointmentBookingInternalContextType = {
  listing: Listing | null;
  offerDetails: GetOfferDetailsType | null;
  partialAppointmentConfig: GetExternalAppointmentConfigType | null;
  authLastPhoneNumber: GetLastPhoneNumberType | null;
  overrideShowingType: OverrideOptionType | null;
  setOverrideShowingType: React.Dispatch<
    React.SetStateAction<OverrideOptionType>
  >;
  isLoading: boolean;
  canBookAppointment: boolean;
  actionButtonsData: ActionButtonData[];
  setActionsButtonData: React.Dispatch<
    React.SetStateAction<ActionButtonData[]>
  >;
  /** True if the appointment type allows exceed the max duration */
  allowExceedMaxDuration: boolean;
  setAllowExceedMaxDuration: React.Dispatch<React.SetStateAction<boolean>>;
  isAdminWithAccess: boolean;
  isListingAgent: boolean;
  hasElevatedAccess: boolean;
  unverifiedModalState: {
    shouldDisplay: boolean;
    redirect: string;
    isAgent: boolean;
  };
  setUnverifiedModalState: React.Dispatch<
    React.SetStateAction<
      AppointmentBookingInternalContextType['unverifiedModalState']
    >
  >;
  requiresBuyerAgreement: boolean;
  setConditionalBuyerAgreementData: (
    data: Omit<Partial<BuyerAgreementData>, 'agreementRequired'>,
  ) => void;
  // From InPersonTabContext, but put API requests together for efficiency
  appointmentConfig: AppointmentConfigJsonType | null;
  /** Date strings (YYYY-MM-DD) of unavailable days for this listing */
  unavailableDateStrings: string[];
  /**
   * If defined, dates after this date is unavailable. Currently this is only tied to sentrilock one day code feature. */
  unavailableAfterDate: Date | null;
  /**
   * List of future appointment data that block appointments from being booked at the same timeslot.
   * Only used for display appointment count logic for a particular timeslot for users with listing access.
   */
  blockingAppointmentList: (Pick<
    EnhancedAppointment,
    'date' | 'status' | 'time' | 'timeEnd'
  > & { coopName: string; coopOrgame?: string })[];
};

const AppointmentBookingInternalContext =
  React.createContext<AppointmentBookingInternalContextType>(
    {} as AppointmentBookingInternalContextType,
  );

export const AppointmentBookingInternalContextProvider: React.FC<
  AppointmentBookingInternalContextProps
> = ({ children, listingId }) => {
  const history = useHistory();
  const { auth } = useContext(AuthContext);

  const [listing, setListing] = useState<Listing | null>(null);
  const [partialAppointmentConfig, setPartialAppointmentConfig] =
    useState<GetExternalAppointmentConfigType | null>(null);
  const [offerDetails, setOfferDetails] = useState<GetOfferDetailsType | null>(
    null,
  );
  const [authLastPhoneNumber, setAuthLastPhoneNumber] =
    useState<GetLastPhoneNumberType | null>(null);
  const [overrideShowingType, setOverrideShowingType] =
    useState<OverrideOptionType | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [canBookAppointment, setCanBookAppointment] = useState<boolean>(true);
  const [actionButtonsData, setActionsButtonData] = useState<
    ActionButtonData[]
  >([]);
  const [allowExceedMaxDuration, setAllowExceedMaxDuration] = useState(false);
  const [unverifiedModalState, setUnverifiedModalState] = useState({
    shouldDisplay: false,
    redirect: '',
    isAgent: true,
  });
  const [buyerAgreementData, setBuyerAgreementData] =
    useState<BuyerAgreementData>({
      agreementRequired: false,
      hasNarBuyerSettlement: false,
      isBuyerShowing: true,
      isRecordShowing: false,
      coopSelected: false,
    });

  // From InPersonTabContext, consolidated API call
  const [appointmentConfig, setAppointmentConfig] =
    useState<AppointmentConfigJsonType | null>(null);
  const [unavailableDateStrings, setUnavailableDateStrings] = useState<
    string[]
  >([]);
  const [unavailableAfterDate, setUnavailableAfterDate] = useState<Date | null>(
    null,
  );
  const [blockingAppointmentList, setBlockingAppointmentList] = useState<
    AppointmentBookingInternalContextType['blockingAppointmentList']
  >([]);

  const setConditionalBuyerAgreementData = useCallback<
    AppointmentBookingInternalContextType['setConditionalBuyerAgreementData']
  >(
    ({ isBuyerShowing, isRecordShowing, coopSelected }) => {
      if (!listing) return;
      const isResidentialTypeListing = ['condo', 'freehold', 'income'].includes(
        listing.type,
      );
      setBuyerAgreementData(prev => {
        const agreementRequired =
          (isBuyerShowing ?? prev.isBuyerShowing) &&
          !(isRecordShowing ?? prev.isRecordShowing) &&
          (coopSelected ?? prev.coopSelected) &&
          isResidentialTypeListing &&
          prev.hasNarBuyerSettlement &&
          featureEnabled('narBuyerSettlementFeature');
        return {
          ...prev,
          agreementRequired,
          ...(isBuyerShowing !== undefined && { isBuyerShowing }),
          ...(isRecordShowing !== undefined && { isRecordShowing }),
          ...(coopSelected !== undefined && { coopSelected }),
        };
      });
    },
    [listing],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const {
          listing: listingData,
          externalConfig: partialAppointmentConfigData,
          offerDetails: offerDetailsData,
          phoneNumber: lastPhoneNumberData,
          portalAssocData,
          unavailableDays: unavailableData,
          appointmentConfig: appointmentConfigData,
          blockedAppointments,
        } = await getAppointmentBookingInfo(listingId);
        setListing(listingData);
        setPartialAppointmentConfig(partialAppointmentConfigData);
        setOfferDetails(offerDetailsData);
        setAuthLastPhoneNumber(lastPhoneNumberData);
        if (featureEnabled('narBuyerSettlementFeature')) {
          setBuyerAgreementData(prev => ({
            ...prev,
            hasNarBuyerSettlement: Boolean(
              portalAssocData?.narSettlementAgreement,
            ),
          }));
        }
        setUnavailableDateStrings(unavailableData.days);
        if (unavailableData.after) {
          setUnavailableAfterDate(
            parseISO(unavailableData.after.toISOString()),
          );
        }
        if (appointmentConfigData) {
          setAppointmentConfig(appointmentConfigData);
        }
        if (blockedAppointments) {
          setBlockingAppointmentList(
            blockedAppointments.map(
              ({ appointment: { coop, date, status, time, timeEnd } }) => ({
                date,
                status,
                time,
                timeEnd,
                coopName: coop.name,
                coopOrgame: coop.org?.name,
              }),
            ),
          );
        }
      } catch (err) {
        errorReporter.error(err);
        message.error('Listing details not found');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [auth, listingId]);

  useEffect(() => {
    if (!listing) return;
    // Set default timezone so that antd components show date/time components in listing timezone
    moment.tz.setDefault(listing.timezone);
    return () => {
      // Reset timezone back to local
      moment.tz.setDefault();
    };
  }, [listing]);

  useEffect(() => {
    if (!listing) return;
    if (isAssumeOrgType(auth.org.type) || auth.org.id === listing.org.id) {
      setCanBookAppointment(true);
      return;
    }
    const isSameMlsType = listing.org.mlsTypes?.some(mlsType =>
      auth.org.mlsTypes?.includes(mlsType),
    );

    setCanBookAppointment(
      Boolean(
        isSameMlsType ||
          isManualCoopUserWithAccess(auth, listing.org as unknown as CachedOrg),
      ),
    );
  }, [auth.org, listing, auth]);

  const isAdminWithAccess = Boolean(
    isAssumeOrgType(auth.org.type) ||
      (ROLES[auth.role] >= ROLES.TEAM_ADMIN &&
        listing &&
        isAuthForRole({
          auth,
          org: listing.org.id,
          location: listing.location,
          locations: listing.locations,
          reps: listing.reps,
        })),
  );
  // For some reason the old booking page (from where this page was adapted from)
  // would check equality by email instead of id
  const isListingAgent = Boolean(
    listing && listing.reps.find(r => r.email === auth.email),
  );
  const hasElevatedAccess = isAdminWithAccess || listing?.canEdit;

  useEffect(() => {
    if (isLoading || !listing) return;

    const isListingUnclaimed = !isFullAccessUser(auth) && listing.isUnclaimed;
    // Shows modal for unverified users, and redirect if they can book showing / offer
    if (auth.isUnverified && !isFullAccessUser(auth)) {
      const isAgent = auth.role === UserRoles.Agent;
      const redirectAccess = (isAgent && !isListingAgent) || isListingUnclaimed;

      setUnverifiedModalState({
        shouldDisplay: true,
        isAgent,
        redirect: redirectAccess ? `#/listing/${listing.id}/view` : '',
      });
      if (redirectAccess) return;
    }

    // Also redirect if listing is unclaimed (no modal designed yet)
    if (isListingUnclaimed) {
      history.replace(`/listing/${listing.id}/view`);
    }
  }, [isLoading, listing, auth, isListingAgent, history]);

  return (
    <AppointmentBookingInternalContext.Provider
      value={{
        listing,
        offerDetails,
        partialAppointmentConfig,
        authLastPhoneNumber,
        overrideShowingType,
        setOverrideShowingType,
        isLoading,
        canBookAppointment,
        actionButtonsData,
        setActionsButtonData,
        allowExceedMaxDuration,
        setAllowExceedMaxDuration,
        isAdminWithAccess,
        isListingAgent,
        hasElevatedAccess: Boolean(hasElevatedAccess),
        unverifiedModalState,
        setUnverifiedModalState,
        requiresBuyerAgreement: buyerAgreementData.agreementRequired,
        setConditionalBuyerAgreementData,
        // From InPersonTabContext, but put API requests together for efficiency
        appointmentConfig,
        unavailableDateStrings,
        unavailableAfterDate,
        blockingAppointmentList,
      }}
    >
      {children}
    </AppointmentBookingInternalContext.Provider>
  );
};
export default AppointmentBookingInternalContext;
